import React, { useMemo } from "react";

function ConversionMetricsPage({
  users,
  auctions,
  dateFilteredAuctions,
  dateFilteredTransactions,
}) {
  const formatPercent = (value) => {
    if (!Number.isFinite(value)) return "0.0%";
    return `${Number(value || 0).toFixed(1)}%`;
  };

  const getUserKey = (user) => user.uid || user.id || user.email || "";

  const metrics = useMemo(() => {
    const registeredUsers = users.length;

    const verifiedUsers = users.filter(
      (user) => user.emailVerified === true || user.phoneVerified === true
    ).length;

    const walletTopUpUsersSet = new Set();

    dateFilteredTransactions.forEach((transaction) => {
      const amount = Number(transaction.amountKWD || transaction.amount || 0);
      const transactionType = String(transaction.type || "").toLowerCase();

      const isFreeGrant =
        transactionType.includes("promo") ||
        transactionType.includes("refund") ||
        transactionType.includes("admin_credit") ||
        transactionType.includes("admin_refund_credit") ||
        transactionType.includes("admin_promotional_credit") ||
        transactionType.includes("add_balance");

      if (
        transaction.status === "succeeded" &&
        amount > 0 &&
        !isFreeGrant &&
        transaction.uid
      ) {
        walletTopUpUsersSet.add(transaction.uid);
      }
    });

    const auctionCreatorSet = new Set();

    dateFilteredAuctions.forEach((auction) => {
      const sellerKey = auction.createdBy || auction.createdByUsername || "";

      if (sellerKey) {
        auctionCreatorSet.add(sellerKey);
      }
    });

    const auctionWinnerSet = new Set();

    dateFilteredAuctions.forEach((auction) => {
      const winnerKey = auction.currentWinner || auction.winner || "";

      if (winnerKey) {
        auctionWinnerSet.add(winnerKey);
      }
    });

    const usersWithCredits = users.filter(
      (user) =>
        Number(user.credits?.["15"] || 0) +
          Number(user.credits?.["30"] || 0) +
          Number(user.credits?.["60"] || 0) +
          Number(user.credits?.ext15 || 0) >
        0
    ).length;

    const usersWithWallet = users.filter(
      (user) => Number(user.walletBalanceKWD || 0) > 0
    ).length;

    const usersWithPaymentsOrWallet =
      new Set([
        ...Array.from(walletTopUpUsersSet),
        ...users
          .filter((user) => Number(user.walletBalanceKWD || 0) > 0)
          .map((user) => getUserKey(user))
          .filter(Boolean),
      ]).size;

    const totalAuctions = dateFilteredAuctions.length;

    const closedAuctions = dateFilteredAuctions.filter(
      (auction) => auction.status === "closed"
    ).length;

    const removedAuctions = dateFilteredAuctions.filter(
      (auction) => auction.status === "removed"
    ).length;

    const auctionsWithWinners = dateFilteredAuctions.filter(
      (auction) => auction.currentWinner || auction.winner
    ).length;

    const auctionsWithBids = dateFilteredAuctions.filter(
      (auction) =>
        Number(auction.currentBid || 0) > 0 ||
        Number(auction.bidHistory?.length || 0) > 0 ||
        Number(auction.bids?.length || 0) > 0
    ).length;

    const winnerDeclaredAuctions = dateFilteredAuctions.filter(
      (auction) => auction.closureReason === "winner_declared"
    ).length;

    const unsoldClosedAuctions = dateFilteredAuctions.filter(
      (auction) =>
        auction.status === "closed" &&
        !auction.currentWinner &&
        !auction.winner
    ).length;

    const reportedAuctions = dateFilteredAuctions.filter(
      (auction) =>
        auction.reported === true || Number(auction.reportCount || 0) > 0
    ).length;

    const flaggedAuctions = dateFilteredAuctions.filter(
      (auction) => auction.flagged === true
    ).length;

    return {
      registeredUsers,
      verifiedUsers,
      walletTopUpUsers: walletTopUpUsersSet.size,
      usersWithPaymentsOrWallet,
      auctionCreators: auctionCreatorSet.size,
      auctionWinners: auctionWinnerSet.size,
      usersWithCredits,
      usersWithWallet,
      totalAuctions,
      closedAuctions,
      removedAuctions,
      auctionsWithWinners,
      auctionsWithBids,
      winnerDeclaredAuctions,
      unsoldClosedAuctions,
      reportedAuctions,
      flaggedAuctions,
      verificationRate:
        registeredUsers > 0 ? (verifiedUsers / registeredUsers) * 100 : 0,
      walletTopUpRate:
        verifiedUsers > 0 ? (walletTopUpUsersSet.size / verifiedUsers) * 100 : 0,
      creatorRate:
        registeredUsers > 0 ? (auctionCreatorSet.size / registeredUsers) * 100 : 0,
      winnerRate:
        registeredUsers > 0 ? (auctionWinnerSet.size / registeredUsers) * 100 : 0,
      auctionCloseRate:
        totalAuctions > 0 ? (closedAuctions / totalAuctions) * 100 : 0,
        auctionWinnerRate:
        totalAuctions > 0 ? (auctionsWithWinners / totalAuctions) * 100 : 0,
      bidParticipationRate:
        totalAuctions > 0 ? (auctionsWithBids / totalAuctions) * 100 : 0,
      winnerDeclaredRate:
        closedAuctions > 0 ? (winnerDeclaredAuctions / closedAuctions) * 100 : 0,
      unsoldRate:
        closedAuctions > 0 ? (unsoldClosedAuctions / closedAuctions) * 100 : 0,
      removedAuctionRate:
        totalAuctions > 0 ? (removedAuctions / totalAuctions) * 100 : 0,
      reportedAuctionRate:
        totalAuctions > 0 ? (reportedAuctions / totalAuctions) * 100 : 0,
      flaggedAuctionRate:
        totalAuctions > 0 ? (flaggedAuctions / totalAuctions) * 100 : 0,
    };
  }, [users, dateFilteredAuctions, dateFilteredTransactions]);

  const funnelRows = [
    {
      metric: "Registered Users",
      count: metrics.registeredUsers,
      conversion: "—",
      meaning: "All user records currently loaded from Firestore.",
    },
    {
      metric: "Verified Users",
      count: metrics.verifiedUsers,
      conversion: formatPercent(metrics.verificationRate),
      meaning: "Users with emailVerified or phoneVerified marked true.",
    },
    {
      metric: "Wallet Top-Up Users",
      count: metrics.walletTopUpUsers,
      conversion: formatPercent(metrics.walletTopUpRate),
      meaning: "Users with successful real-money top-up in Global Date Range.",
    },
    {
      metric: "Auction Creators",
      count: metrics.auctionCreators,
      conversion: formatPercent(metrics.creatorRate),
      meaning: "Users who created auctions in Global Date Range.",
    },
    {
      metric: "Auction Winners",
      count: metrics.auctionWinners,
      conversion: formatPercent(metrics.winnerRate),
      meaning: "Users who won auctions in Global Date Range.",
    },
  ];

  const auctionRows = [
    {
      metric: "Total Auctions",
      count: metrics.totalAuctions,
      conversion: "—",
      meaning: "Auctions matching Global Date Range.",
    },
    {
      metric: "Closed Auctions",
      count: metrics.closedAuctions,
      conversion: formatPercent(metrics.auctionCloseRate),
      meaning: "Closed auctions compared with total filtered auctions.",
    },
    {
      metric: "Auctions With Winners",
      count: metrics.auctionsWithWinners,
      conversion: formatPercent(metrics.auctionWinnerRate),
      meaning: "Auctions that have currentWinner or winner.",
    },
    {
      metric: "Auctions With Bids",
      count: metrics.auctionsWithBids,
      conversion: formatPercent(metrics.bidParticipationRate),
      meaning: "Auctions that received at least one bid.",
    },
    {
      metric: "Winner Declared Auctions",
      count: metrics.winnerDeclaredAuctions,
      conversion: formatPercent(metrics.winnerDeclaredRate),
      meaning: "Closed auctions where creator declared a winner.",
    },
    {
      metric: "Unsold Closed Auctions",
      count: metrics.unsoldClosedAuctions,
      conversion: formatPercent(metrics.unsoldRate),
      meaning: "Closed auctions with no winner.",
    },
    {
      metric: "Removed Auctions",
      count: metrics.removedAuctions,
      conversion: formatPercent(metrics.removedAuctionRate),
      meaning: "Removed auctions compared with total filtered auctions.",
    },
    {
      metric: "Reported Auctions",
      count: metrics.reportedAuctions,
      conversion: formatPercent(metrics.reportedAuctionRate),
      meaning: "Reported auctions compared with total filtered auctions.",
    },
    {
      metric: "Flagged Auctions",
      count: metrics.flaggedAuctions,
      conversion: formatPercent(metrics.flaggedAuctionRate),
      meaning: "Flagged auctions compared with total filtered auctions.",
    },
  ];

  return (
    <>
      <section className="stats-grid">
        <div className="stat-card">
          <span>Verification Rate</span>
          <strong>{formatPercent(metrics.verificationRate)}</strong>
          <small style={{ display: "block", marginTop: 4 }}>
            Verified users / registered users
          </small>
        </div>

        <div className="stat-card">
          <span>Wallet Top-Up Conversion</span>
          <strong>{formatPercent(metrics.walletTopUpRate)}</strong>
          <small style={{ display: "block", marginTop: 4 }}>
            Top-up users / verified users
          </small>
        </div>

        <div className="stat-card">
          <span>Creator Conversion</span>
          <strong>{formatPercent(metrics.creatorRate)}</strong>
          <small style={{ display: "block", marginTop: 4 }}>
            Auction creators / registered users
          </small>
        </div>

        <div className="stat-card">
          <span>Winner Conversion</span>
          <strong>{formatPercent(metrics.winnerRate)}</strong>
          <small style={{ display: "block", marginTop: 4 }}>
            Auction winners / registered users
          </small>
        </div>

        <div className="stat-card">
          <span>Auction Close Rate</span>
          <strong>{formatPercent(metrics.auctionCloseRate)}</strong>
          <small style={{ display: "block", marginTop: 4 }}>
            Closed auctions / filtered auctions
          </small>
        </div>

        <div className="stat-card">
          <span>Auction Winner Rate</span>
          <strong>{formatPercent(metrics.auctionWinnerRate)}</strong>
          <small style={{ display: "block", marginTop: 4 }}>
            Auctions with winners / filtered auctions
          </small>
        </div>

        <div className="stat-card">
          <span>Bid Participation Rate</span>
          <strong>{formatPercent(metrics.bidParticipationRate)}</strong>
          <small style={{ display: "block", marginTop: 4 }}>
            Auctions with bids / filtered auctions
          </small>
        </div>

        <div className="stat-card">
          <span>Unsold Rate</span>
          <strong>{formatPercent(metrics.unsoldRate)}</strong>
          <small style={{ display: "block", marginTop: 4 }}>
            Closed auctions without winner / closed auctions
          </small>
        </div>
      </section>

      <section className="table-card">
        <div className="table-header">
          <div>
            <h2>User Conversion Funnel</h2>
            <p>
              Tracks how users move from registration to payment, selling, and winning.
            </p>
          </div>
        </div>

        <div className="table-wrapper">
          <table>
            <thead>
              <tr>
                <th>Metric</th>
                <th>Count</th>
                <th>Conversion</th>
                <th>Meaning</th>
              </tr>
            </thead>

            <tbody>
              {funnelRows.map((row) => (
                <tr key={row.metric}>
                  <td>{row.metric}</td>
                  <td>{row.count}</td>
                  <td>{row.conversion}</td>
                  <td className="small-muted">{row.meaning}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="table-card">
        <div className="table-header">
          <div>
            <h2>Auction Conversion Metrics</h2>
            <p>
              Tracks auction outcomes, winner rate, removal rate, reports, and flags.
            </p>
          </div>
        </div>

        <div className="table-wrapper">
          <table>
            <thead>
              <tr>
                <th>Metric</th>
                <th>Count</th>
                <th>Rate</th>
                <th>Meaning</th>
              </tr>
            </thead>

            <tbody>
              {auctionRows.map((row) => (
                <tr key={row.metric}>
                  <td>{row.metric}</td>
                  <td>{row.count}</td>
                  <td>{row.conversion}</td>
                  <td className="small-muted">{row.meaning}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </>
  );
}

export default ConversionMetricsPage;